import { useMemo, useRef } from 'react'
import * as Yup from 'yup'

import { useUserProfile } from 'hooks/useUserProfile'
import { useAmlQuestions } from 'hooks/useCompliance'
import {
  API_DATE_FORMAT,
  UI_DATE_FORMAT,
  formatDate,
  formatFieldToLabel
} from 'utils/util'
import formValidation, {
  VALIDATION_VERIFY_STEP_ONE,
  VALIDATION_VERIFY_STEP_TWO
} from './formValidation'

import {
  verifyFinancialDetailsPath,
  verifyGetStartedPath,
  verifyIdDetailsPath,
  verifyPersonalDetailsPath
} from 'components/Routes'

import DefaultLayout from 'components/layout/DefaultLayout'
import { Formik } from 'formik'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import Breadcrumbs from './Breadcrumbs/Breadcrumbs'
import FinancialDetailsForm from './FinancialDetailsForm'
import GetStarted from './GetStarted/GetStarted'
import IdDetailsForm from './IdDetailsForm'
import PersonalDetailsForm from './PersonalDetailsForm'

export interface FormValues {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
  firstName: string
  middleName: string
  lastName: string
  preferredName: string
  dateOfBirth: string
  addressLine1: string
  addressLine2: string
  suburb: string
  city: string
  postcode: string
  nzMobilePhoneNumber: string
  driversLicenceNumber?: string
  driversLicenceVersion?: string
  // Per-question elaboration when a chosen AML answer option has
  // comments_required — keyed by question key, submitted as
  // AmlAnswerIn.comments
  comments: { [key: string]: string }
  passportNumber?: string
  passportExpiry?: string
  verificationMethod?: string
  ethnicity: string
}

// TODO: dynamic form validation schema for AML questions

export default function OnboardingWizard(): JSX.Element {
  const location = useLocation()
  const financialDetailsFormRef = useRef<{
    handleClickVerify: () => void
  } | null>(null)

  const { data: profileData, isLoading: profileLoading } = useUserProfile()

  const { data: amlData, isLoading: amlLoading } = useAmlQuestions()

  const initialValues = useMemo(
    () => {
      let values = {
        ethnicity: '' // Default ethnicity to avoid MUI Select warnings
      }

      if (profileData) {
        const {
          address_line1,
          address_line2,
          city,
          date_of_birth,
          first_name,
          middle_name,
          last_name,
          mobile_phone_number,
          postcode,
          preferred_name,
          suburb
        } = profileData

        values = {
          ...values,
          addressLine1: address_line1 || '',
          addressLine2: address_line2 || '',
          city: city || '',
          dateOfBirth: date_of_birth
            ? formatDate({
                date: date_of_birth,
                inputFormat: API_DATE_FORMAT,
                outputFormat: UI_DATE_FORMAT
              })
            : '',
          driversLicenceNumber: '',
          driversLicenceVersion: '',
          firstName: first_name || '',
          lastName: last_name || '',
          middleName: middle_name || '',
          nzMobilePhoneNumber: mobile_phone_number || '',
          postcode: postcode || '',
          preferredName: preferred_name || '',
          suburb: suburb || '',
          passportExpiry: '',
          passportNumber: '',
          verificationMethod: undefined,
          comments: {}
        } as FormValues

        // Seed AML answers from prior_answer if available
        if (amlData?.questions) {
          const amlValues = amlData.questions.reduce(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (acc: any, question) => {
              acc[question.key] = question.prior_answer || ''
              return acc
            },
            {}
          )
          values = { ...values, ...amlValues }
        }
      }

      return values as FormValues
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profileData, amlData]
  )

  const validationSchema = useMemo(() => {
    /* We validate Financial Details Form in Frontend because in BackEnd doesn't response error message for each question field */
    const VALIDATION_VERIFY_STEP_THREE = amlData?.questions?.length
      ? amlData.questions.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: Record<string, any>, question) => {
            acc[question.key] = Yup.string().required(
              `${formatFieldToLabel(question.key)} is required`
            )
            return acc
          },
          {}
        )
      : {}

    switch (location.pathname) {
      // Validation Personal Details form (Step 1/3)
      case verifyPersonalDetailsPath():
        return Yup.object().shape(VALIDATION_VERIFY_STEP_ONE)
      // Validation ID Details form (Step 2/3)
      case verifyIdDetailsPath():
        return Yup.object().shape(VALIDATION_VERIFY_STEP_TWO)
      // Validation Financial Details form (Step 3/3)
      case verifyFinancialDetailsPath():
        return Yup.object().shape(VALIDATION_VERIFY_STEP_THREE)
      default:
        return Yup.object().shape(formValidation)
    }
  }, [location.pathname, amlData])

  const pageHeading = useMemo(() => {
    switch (location.pathname) {
      case verifyGetStartedPath():
        return <Breadcrumbs step={0} heading='get started' />
      case verifyPersonalDetailsPath():
        return <Breadcrumbs step={1} heading='introduce yourself' />
      case verifyIdDetailsPath():
        return <Breadcrumbs step={2} heading='id check' />
      case verifyFinancialDetailsPath():
        return <Breadcrumbs step={3} heading='more details' />
      default:
        return ''
    }
  }, [location.pathname])

  return (
    <DefaultLayout
      heading={pageHeading}
      wrapperContainer={{ position: 'initial' }}
      wrapperContent={{ mt: 4 }}
      loading={profileLoading || amlLoading}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={() => {
          financialDetailsFormRef.current?.handleClickVerify()
        }}
        validationSchema={validationSchema}
      >
        <div>
          <Routes>
            <Route index element={<Navigate to='get_started' replace />} />
            <Route path='get_started' element={<GetStarted />} />
            <Route path='personal_details' element={<PersonalDetailsForm />} />
            <Route path='id_details' element={<IdDetailsForm />} />
            <Route
              path='financial_details'
              element={<FinancialDetailsForm ref={financialDetailsFormRef} />}
            />
          </Routes>
        </div>
      </Formik>
    </DefaultLayout>
  )
}
